import { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Box,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import {
  getAvailableMetrics,
  getAvailablePackages,
  getMetricDisplayName,
  getPackageDisplayName,
  getPlatformDisplayName,
  isLanguageSpecific,
  getProduct,
  PRODUCTS,
} from "@/utils/social";
import {
  type LinkData,
  type Addon,
  MetricType,
  PackageType,
  type Product,
  ProductId,
  Network,
} from "@commercial-publishing/shared-types/social";
import { Language, type Maybe } from "@commercial-publishing/shared-types";

interface Props {
  onSave: (selection: Product) => void;
  onCancel: () => void;
  /** Optional selection to edit */
  initialSelection?: Product;
  /** Instagram account options */
  instaOptions?: { id: string; username: string }[];
}

const ProductSelector = ({
  onSave,
  onCancel,
  initialSelection,
  instaOptions,
}: Props) => {
  // TODO: use regex from shared-types
  const igPostRegex = /instagram\.com/;

  const [selectedProductId, setSelectedProductId] =
    useState<Maybe<ProductId>>(undefined);
  const [selectedMetric, setSelectedMetric] =
    useState<Maybe<MetricType>>(undefined);
  const [selectedPackage, setSelectedPackage] =
    useState<Maybe<PackageType>>(undefined);
  const [selectedLanguage, setSelectedLanguage] =
    useState<Maybe<Language>>(undefined);
  const [socialLinks, setSocialLinks] = useState<LinkData[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Get the selected product configuration
  const selectedProduct = selectedProductId
    ? getProduct(selectedProductId)
    : null;

  // Populate state when an initial selection is provided (edit mode)
  useEffect(() => {
    if (initialSelection) {
      setSelectedProductId(initialSelection.productId);
      setSelectedMetric(initialSelection.kpiMetric);
      setSelectedPackage(initialSelection.packageType);
      setSelectedLanguage(initialSelection.language);
      setSocialLinks(initialSelection.links ?? []);
      setAddons(initialSelection.addons ?? []);
    }
  }, [initialSelection]);

  const handleProductChange = (productId: ProductId) => {
    setSelectedProductId(productId);
    setSelectedMetric(undefined);
    setSelectedPackage(undefined);
    setSelectedLanguage(undefined);
    setSocialLinks([]); // Reset social links
    setAddons([]); // Reset addons
    setErrors({});
  };

  const handleMetricChange = (metric: MetricType) => {
    setSelectedMetric(metric);
    setSelectedPackage(undefined);
    setSelectedLanguage(undefined);
    setErrors({});
  };

  const handlePackageChange = (packageType: PackageType) => {
    setSelectedPackage(packageType);
    setSelectedLanguage(undefined);
    setErrors({});
  };

  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);
  };

  const addSocialLink = () => {
    setSocialLinks([...socialLinks, { socialLink: "", instagramAccount: "" }]);
  };

  const removeSocialLink = (index: number) => {
    const newLinks = socialLinks.filter((_, i) => i !== index);
    setSocialLinks(newLinks);
  };

  const editSocialLink = (index: number, value: string) => {
    const newLinks = [...socialLinks];
    newLinks[index] = { ...newLinks[index], socialLink: value };
    setSocialLinks(newLinks);
  };

  const editInstaAccount = (index: number, instagramAccount: string) => {
    const newLinks = [...socialLinks];
    newLinks[index] = { ...newLinks[index], instagramAccount };
    setSocialLinks(newLinks);
  };

  const addAddon = () => {
    setAddons([
      ...addons,
      { network: Network.Instagram, metric: MetricType.Impressions, value: 0 },
    ]);
  };

  const removeAddon = (index: number) => {
    const newAddons = addons.filter((_, i) => i !== index);
    setAddons(newAddons);
  };

  const editAddon = (
    index: number,
    field: keyof Addon,
    value: Network | string | number,
  ) => {
    const newAddons = [...addons];
    newAddons[index] = { ...newAddons[index], [field]: value };
    setAddons(newAddons);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!selectedProduct) {
      newErrors.product = "Please select a product";
    }

    if (!selectedMetric) {
      newErrors.metric = "Please select a metric";
    }

    if (!selectedPackage) {
      newErrors.package = "Please select a package";
    }

    if (selectedProduct && selectedMetric && selectedPackage) {
      const needsLanguage =
        selectedProduct.packages[selectedPackage] &&
        selectedProduct.packages[selectedPackage]![selectedMetric] &&
        isLanguageSpecific(
          selectedProduct.packages[selectedPackage]![selectedMetric]!,
        );

      if (needsLanguage && !selectedLanguage) {
        newErrors.language = "Please select a language";
      }

      if (!socialLinks || socialLinks.length === 0) {
        newErrors.socialLinks = "Please add at least one social link";
      } else {
        // Validate URLs
        const invalidLinks = socialLinks.filter(
          (linkData) => linkData.socialLink && !isValidUrl(linkData.socialLink),
        );
        if (invalidLinks.length > 0) {
          newErrors.socialLinks = "Please enter valid URLs";
        }

        // Validate Instagram account selection for Instagram links
        const instagramLinksWithoutAccount = socialLinks.filter(
          (linkData) =>
            linkData.socialLink &&
            linkData.socialLink.match(igPostRegex) &&
            !linkData.instagramAccount,
        );
        if (instagramLinksWithoutAccount.length > 0) {
          newErrors.instagramAccount =
            "Please select Instagram account for all Instagram links";
        }

        // Validate empty social links
        const emptyLinks = socialLinks.filter(
          (linkData) => !linkData.socialLink.trim(),
        );
        if (emptyLinks.length > 0) {
          newErrors.emptyLinks =
            "Please fill in all social link fields or remove empty ones";
        }
      }

      // Validate addons for SocialCrossmediaTeaser
      if (
        selectedProductId === ProductId.SocialCrossmediaTeaser &&
        addons.length > 0
      ) {
        const invalidAddons = addons.filter(
          (addon) => !addon.network || !addon.metric || addon.value < 0,
        );
        if (invalidAddons.length > 0) {
          newErrors.addons =
            "Please ensure all addons have a network, metric, and value greater or equal 0";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const addProduct = () => {
    if (validateForm()) {
      const selection: Product = {
        productId: selectedProductId!,
        links: socialLinks,
        posts: [],
        kpiMetric: selectedMetric!,
        packageType: selectedPackage!,
        language: selectedLanguage!,
        addons: addons.length > 0 ? addons : undefined,
      };
      onSave(selection);
    }
  };

  const availableMetrics = selectedProduct
    ? getAvailableMetrics(selectedProduct)
    : [];
  const availablePackages = selectedProduct
    ? getAvailablePackages(selectedProduct)
    : [];
  const availableLanguages = [Language.German, Language.French];

  // Check if current selection requires language choice
  const needsLanguageSelection =
    selectedProduct &&
    selectedMetric &&
    selectedPackage &&
    selectedProduct.packages[selectedPackage] &&
    selectedProduct.packages[selectedPackage]![selectedMetric] &&
    isLanguageSpecific(
      selectedProduct.packages[selectedPackage]![selectedMetric]!,
    );

  // Check if we should show social links section
  const showSocialLinks =
    selectedProduct &&
    selectedMetric &&
    selectedPackage &&
    (!needsLanguageSelection || selectedLanguage);

  return (
    <Grid container spacing={3}>
      {/* Product Selection */}
      <Grid item xs={12}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Choose Product
        </Typography>
        {errors.product && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {errors.product}
          </Typography>
        )}
        <Grid container spacing={2}>
          {Object.entries(PRODUCTS).map(([productId, product], index) => (
            <Grid item xs={12} md={6} key={productId}>
              <Card
                sx={{
                  height: "100%",
                  border: selectedProductId === productId ? 2 : 1,
                  borderColor:
                    selectedProductId === productId
                      ? "primary.main"
                      : "grey.300",
                  backgroundColor:
                    selectedProductId === productId
                      ? "primary.50"
                      : "background.paper",
                }}
              >
                <CardActionArea
                  onClick={() => handleProductChange(productId as ProductId)}
                  autoFocus={index === 0 && !!initialSelection}
                  sx={{ height: "100%" }}
                >
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: "semibold", mb: 1 }}
                    >
                      {product.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {product.description}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {product.platforms.map((platform) => (
                        <Chip
                          key={platform}
                          label={getPlatformDisplayName(platform)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Metric Selection */}
      {selectedProduct && (
        <Grid item xs={12}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Choose Metric
          </Typography>
          {errors.metric && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {errors.metric}
            </Typography>
          )}
          <Grid container spacing={2}>
            {availableMetrics.map((metric) => (
              <Grid item xs={12} sm={6} md={4} key={metric}>
                <Card
                  sx={{
                    border: selectedMetric === metric ? 2 : 1,
                    borderColor:
                      selectedMetric === metric ? "success.main" : "grey.300",
                    backgroundColor:
                      selectedMetric === metric
                        ? "success.50"
                        : "background.paper",
                  }}
                >
                  <CardActionArea onClick={() => handleMetricChange(metric)}>
                    <CardContent sx={{ py: 2 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "medium" }}
                        >
                          {getMetricDisplayName(metric)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Package Selection */}
      {selectedProduct && selectedMetric && (
        <Grid item xs={12}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Choose Package
          </Typography>
          {errors.package && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {errors.package}
            </Typography>
          )}
          <Grid container spacing={2}>
            {availablePackages.map((packageType) => (
              <Grid item xs={12} sm={6} md={3} key={packageType}>
                <Card
                  sx={{
                    border: selectedPackage === packageType ? 2 : 1,
                    borderColor:
                      selectedPackage === packageType
                        ? "secondary.main"
                        : "grey.300",
                    backgroundColor:
                      selectedPackage === packageType
                        ? "secondary.50"
                        : "background.paper",
                  }}
                >
                  <CardActionArea
                    onClick={() => handlePackageChange(packageType)}
                  >
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {/*TODO: useMemo */}
                        {getPackageDisplayName(packageType)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Language Selection */}
      {needsLanguageSelection && (
        <Grid item xs={12}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Choose Language
          </Typography>
          {errors.language && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {errors.language}
            </Typography>
          )}
          <Grid container spacing={2}>
            {availableLanguages.map((language) => (
              <Grid item xs={12} sm={6} key={language}>
                <Card
                  sx={{
                    border: selectedLanguage === language ? 2 : 1,
                    borderColor:
                      selectedLanguage === language
                        ? "warning.main"
                        : "grey.300",
                    backgroundColor:
                      selectedLanguage === language
                        ? "warning.50"
                        : "background.paper",
                  }}
                >
                  <CardActionArea
                    onClick={() => handleLanguageChange(language)}
                  >
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {language === Language.German ? "German" : "French"}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Social Links Section */}
      {showSocialLinks && (
        <Grid item xs={12}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Social Links
          </Typography>
          {errors.socialLinks && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {errors.socialLinks}
            </Typography>
          )}
          {errors.instagramAccount && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {errors.instagramAccount}
            </Typography>
          )}
          {errors.emptyLinks && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {errors.emptyLinks}
            </Typography>
          )}

          {socialLinks.length === 0 ? (
            <Paper
              sx={{ p: 3, textAlign: "center", backgroundColor: "grey.50" }}
            >
              <Typography variant="body2" color="text.secondary">
                No social links added yet. Click the + button to add your first
                link.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {socialLinks.map((linkData, index) => (
                <Grid item xs={12} key={index}>
                  <FormControl fullWidth variant="outlined">
                    {/* URL Field */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <TextField
                        fullWidth
                        type="url"
                        label={`Social Link ${index + 1}`}
                        value={linkData.socialLink}
                        onChange={(e) => editSocialLink(index, e.target.value)}
                        variant="outlined"
                        InputLabelProps={{
                          style: { color: "#aeb5bd" },
                        }}
                      />
                      <IconButton
                        onClick={() => removeSocialLink(index)}
                        color="error"
                        sx={{ flexShrink: 0, mt: 1 }}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Box>

                    {/* Instagram Account Dropdown - only show for Instagram links */}
                    {linkData.socialLink.match(igPostRegex) && (
                      <FormControl variant="outlined" sx={{ maxWidth: 200 }}>
                        <InputLabel>Instagram Account</InputLabel>
                        <Select
                          value={linkData.instagramAccount || ""}
                          onChange={(e) =>
                            editInstaAccount(index, e.target.value as string)
                          }
                          label="Instagram Account"
                          MenuProps={{
                            PaperProps: {
                              sx: { maxHeight: 200, maxWidth: 200 },
                            },
                          }}
                        >
                          {instaOptions?.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.username}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          )}

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              disabled={
                socialLinks.length >= (selectedProduct?.maxNumberOfLinks || 0)
              }
              onClick={addSocialLink}
              color="primary"
              size="large"
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Box>
        </Grid>
      )}

      {/* Addons Section - Only for SocialCrossmediaTeaser */}
      {showSocialLinks &&
        selectedProductId === ProductId.SocialCrossmediaTeaser && (
          <Grid item xs={12}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              Addons (Optional)
            </Typography>
            {errors.addons && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {errors.addons}
              </Typography>
            )}

            {addons.length === 0 ? (
              <Paper
                sx={{ p: 3, textAlign: "center", backgroundColor: "grey.50" }}
              >
                <Typography variant="body2" color="text.secondary">
                  No addons added yet. Click the + button to add an addon.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {addons.map((addon, index) => (
                  <Grid item xs={12} key={index}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Network</InputLabel>
                        <Select
                          value={addon.network}
                          onChange={(e) =>
                            editAddon(
                              index,
                              "network",
                              e.target.value as Network,
                            )
                          }
                          label="Network"
                        >
                          {selectedProduct?.platforms.map((platform) => (
                            <MenuItem key={platform} value={platform}>
                              {getPlatformDisplayName(platform)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 150 }}>
                        <InputLabel>Metric</InputLabel>
                        <Select
                          value={addon.metric}
                          onChange={(e) =>
                            editAddon(index, "metric", e.target.value)
                          }
                          label="Metric"
                        >
                          <MenuItem value="impressions">Impressions</MenuItem>
                          <MenuItem value="clicks">Clicks</MenuItem>
                          <MenuItem value="views">Views</MenuItem>
                          <MenuItem value="linkClicks">Link Clicks</MenuItem>
                          <MenuItem value="videoViews">Video Views</MenuItem>
                          <MenuItem value="storyViews">Story Views</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        type="number"
                        label="Value"
                        value={addon.value}
                        onChange={(e) =>
                          editAddon(
                            index,
                            "value",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        variant="outlined"
                        sx={{ minWidth: 120 }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />

                      <IconButton
                        onClick={() => removeAddon(index)}
                        color="error"
                        sx={{ flexShrink: 0, mt: 1 }}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <IconButton onClick={addAddon} color="primary" size="large">
                <AddCircleOutlineIcon />
              </IconButton>
            </Box>
          </Grid>
        )}

      <Grid item xs={12}>
        {selectedProduct &&
          selectedMetric &&
          selectedPackage &&
          (!needsLanguageSelection || selectedLanguage) && (
            <Button
              variant="outlined"
              onClick={addProduct}
              style={{
                textTransform: "none",
                color: "white",
                minHeight: "47px",
                minWidth: "163px",
                backgroundColor: "#223647",
                fontSize: "16px",
                fontWeight: "700",
              }}
              sx={{ mr: 2 }}
            >
              Save
            </Button>
          )}
        <Button
          variant="outlined"
          onClick={onCancel}
          style={{
            textTransform: "none",
            color: "white",
            minHeight: "47px",
            minWidth: "163px",
            backgroundColor: "#223647",
            fontSize: "16px",
            fontWeight: "700",
          }}
        >
          Cancel
        </Button>
      </Grid>
    </Grid>
  );
};

export default ProductSelector;
